import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { useUsernameAvailability } from '@/hooks/use-username-availability';
import {
  formatInviteCode,
  validateEmail,
  validateInviteCode,
  validatePassword,
  validateUsername,
} from '@/utils/auth-validation';
import { router } from 'expo-router';
import { useCallback, useState } from 'react';

export function useRegisterForm() {
  const { register } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { usernameAvailable, checkingUsername } = useUsernameAvailability(username);

  // Whether the invite code passes the client-side format check. Drives whether
  // the social sign-in buttons are enabled (a valid invite must precede OAuth).
  const inviteValid = validateInviteCode(inviteCode) === null;

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleUsernameChange = useCallback((text: string) => {
    const formatted = text.toLowerCase().replace(/[^a-z0-9]/g, '');
    setUsername(formatted);
  }, []);

  const handleInviteCodeChange = useCallback((text: string) => {
    setInviteCode(formatInviteCode(text));
  }, []);

  const validateInputs = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    const nameError = !name.trim() ? 'Name is required' : null;
    if (nameError) newErrors.name = nameError;

    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;

    const usernameError = validateUsername(username);
    if (usernameError) {
      newErrors.username = usernameError;
    } else if (usernameAvailable === false) {
      newErrors.username = 'This username is already taken';
    } else if (usernameAvailable === null && !checkingUsername) {
      newErrors.username = 'Unable to check username availability';
    }

    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    const inviteError = validateInviteCode(inviteCode);
    if (inviteError) newErrors.inviteCode = inviteError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, email, username, password, confirmPassword, inviteCode, usernameAvailable, checkingUsername]);

  const handleRegister = useCallback(async () => {
    if (!validateInputs()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const result = await register(
        email.trim(),
        password,
        name.trim(),
        username.trim().toLowerCase(),
        inviteCode.trim(),
      );

      if (result.error) {
        const errorMessage = result.error.message || '';
        const errorStatus = result.error.status;
        const errorCode = result.error.code;

        const isDuplicateEmail =
          errorStatus === 422 || errorCode === 'USER_ALREADY_EXISTS';

        // The auth service raises every invite failure as a bare 400 with a human
        // message and no machine-readable code (auth-service auth-config.ts:142-153),
        // so the message is the only signal available. Match on "invite" alone --
        // the old check also matched "code", which caught any unrelated message
        // containing the word and pinned it to the invite field.
        const isInviteCodeError =
          errorStatus === 400 && errorMessage.toLowerCase().includes('invite');

        if (isDuplicateEmail) {
          setErrors({ email: 'An account with this email already exists. Try logging in instead.' });
        } else if (isInviteCodeError) {
          setErrors({ inviteCode: errorMessage });
        } else {
          toast.error('Could not create your account', {
            description: errorMessage || 'Check your details and try again.',
          });
        }
        setIsLoading(false);
      } else {
        router.replace({ pathname: '/(auth)/verify-otp', params: { email: email.trim() } });
      }
    } catch {
      toast.error('Could not create your account', {
        description: 'Check your connection and try again.',
      });
      setIsLoading(false);
    }
  }, [validateInputs, register, email, password, name, username, inviteCode]);

  const navigateToLogin = useCallback(() => {
    router.push({ pathname: '/(auth)/login' });
  }, []);

  return {
    name,
    setName,
    email,
    setEmail,
    username,
    handleUsernameChange,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    inviteCode,
    handleInviteCodeChange,
    inviteValid,
    isLoading,
    errors,
    clearFieldError,
    usernameAvailable,
    checkingUsername,
    handleRegister,
    navigateToLogin,
  };
}
